import { createFileRoute, Link } from "@tanstack/react-router";

import { PageChrome } from "../components/PageChrome";
import { ARTICLES, articlePath, readingMinutes } from "../lib/content";
import { QUIZZES, quizPath } from "../lib/quiz";
import {
  breadcrumbJsonLd,
  canonical,
  collectionJsonLd,
  documentHeaders,
  socialMeta,
} from "../lib/seo";
import { VERSE_TOPICS, versePath } from "../lib/verses";

const TITLE = "Phones, prayer and paying attention — Bible Shield";
const DESCRIPTION =
  "Practical writing on screen time, evening habits and scripture — from the people who make Bible Shield, the christian app blocker you pray to unlock.";

/**
 * `/blog` — the index.
 *
 * ── Why this page exists, beyond listing things ──────────────────────────────
 *
 * It is the hub that makes the articles crawlable. A page with no links to it
 * is a page Google finds late and treats as unimportant, and the articles are
 * otherwise reachable only from the sitemap and from each other. This is linked
 * from the footer of every page on the site, which is what turns four orphans
 * into a section.
 *
 * ── Why it is English-only ───────────────────────────────────────────────────
 *
 * The landing page is in nine languages because it is one page and the app
 * speaks nine. Articles are not one page, and machine-translating a growing
 * corpus into eight more is precisely the "scaled content" Google's own
 * guidance warns about — with the twist that the penalty is SITE-WIDE, so a
 * pile of thin translations would drag down the nine landing pages that
 * currently work. English first; a second language when an article has earned
 * one by actually being read.
 */
export const Route = createFileRoute("/blog/")({
  headers: documentHeaders,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...socialMeta({
        title: TITLE,
        description: DESCRIPTION,
        path: "/blog",
        lang: "en",
      }),
      {
        "script:ld+json": breadcrumbJsonLd([
          { name: "Bible Shield", path: "/" },
          { name: "Blog", path: "/blog" },
        ]),
      },
      // What the links on this page ARE, so a crawler files /blog as a hub
      // rather than as one more leaf with links on it.
      {
        "script:ld+json": collectionJsonLd({
          name: TITLE,
          description: DESCRIPTION,
          path: "/blog",
          items: ARTICLES.map((a) => ({
            name: a.h1,
            path: articlePath(a.slug),
          })),
        }),
      },
    ],
    links: [
      { rel: "canonical", href: canonical("/blog") },
      // How a reader, an aggregator or a browser extension discovers the feed.
      // A feed nobody can find from the page it describes is a file on a server.
      {
        rel: "alternate",
        type: "application/rss+xml",
        title: "Bible Shield — writing",
        href: canonical("/rss.xml"),
      },
    ],
  }),
  component: BlogIndex,
});

function BlogIndex() {
  return (
    <PageChrome>
      <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
        writing
      </p>
      <h1 className="mt-3 font-display text-[clamp(1.9rem,6vw,2.9rem)] leading-[1.15] font-bold tracking-tight text-ink text-balance">
        Phones, prayer and paying attention
      </h1>
      <p className="mt-4 max-w-[60ch] text-lg leading-relaxed text-ink2">
        What we have learned building an app that stands between people and
        their phones — and what actually works when the block fires.
      </p>

      <ul className="mt-10 grid gap-5 sm:grid-cols-2">
        {ARTICLES.map((article) => (
          <li key={article.slug}>
            <Link
              to="/blog/$slug"
              params={{ slug: article.slug }}
              className="brutal flex h-full flex-col gap-3 p-6 transition-transform hover:-translate-y-0.5"
            >
              <span className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
                {readingMinutes(article)} min read
              </span>
              <span className="font-display text-xl leading-snug font-bold text-ink">
                {article.h1}
              </span>
              <span className="leading-relaxed text-body">
                {article.description}
              </span>
            </Link>
          </li>
        ))}
      </ul>

      {/* The other half of the section, and the reason both halves work: an
          article is read and a quiz is forwarded, so each one is the other's
          distribution. A reader who finishes a piece here is one click from the
          thing they will actually send to somebody. */}
      <section className="mt-14 border-t-[2.5px] border-ink pt-10">
        <h2 className="font-display text-2xl font-bold text-ink">
          Or take a test
        </h2>
        <p className="mt-3 max-w-[60ch] leading-relaxed text-ink2">
          Every question and every result is on the page before you start,
          nothing is stored, and none of them ends by telling you that you are
          wonderful.
        </p>
        <ul className="mt-5 grid gap-4 sm:grid-cols-2">
          {QUIZZES.map((quiz) => (
            <li key={quiz.slug}>
              <a
                href={quizPath(quiz.slug)}
                className="brutal flex h-full flex-col gap-2 p-5 transition-transform hover:-translate-y-0.5"
              >
                <span className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
                  {quiz.questions.length} questions
                </span>
                <span className="font-display text-lg leading-snug font-bold text-ink">
                  {quiz.h1}
                </span>
              </a>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-ink">
          Or look up the passages
        </h2>
        <p className="mt-3 max-w-[60ch] leading-relaxed text-ink2">
          Six collections, ten passages each, quoted in full from the
          public-domain World English Bible with a note on what every one of
          them actually says.
        </p>
        <ul className="mt-5 flex flex-wrap gap-3">
          {VERSE_TOPICS.map((topic) => (
            <li key={topic.slug}>
              <a
                href={versePath(topic.slug)}
                className="inline-flex rounded-pill border-[2.5px] border-ink bg-surface px-4 py-2 font-display text-sm font-bold text-ink transition-colors hover:bg-brand-wash"
              >
                {topic.h1.replace(/^Bible verses /, "")}
              </a>
            </li>
          ))}
        </ul>
      </section>
    </PageChrome>
  );
}
